<template>
    <div>
        <b-row class="mb-3 align-items-end">
            <b-col md="4">
                <b-form-group label="업체 ID 필터 (선택)" class="mb-0">
                    <b-form-input v-model="companyFilter" type="number" placeholder="예: 34090"
                                  :disabled="loading" @keyup.enter="submitFilter"
                    />
                </b-form-group>
            </b-col>
            <b-col md="3">
                <b-button variant="outline-primary" :disabled="loading" @click="submitFilter">
                    <b-spinner v-if="loading" small class="mr-1"/>
                    조회
                </b-button>
                <b-button v-if="companyFilter" variant="link" :disabled="loading" @click="clearFilter">
                    필터 해제
                </b-button>
            </b-col>
        </b-row>

        <!-- 목록 API 는 최근 N건만 준다(offset 없음, 최대 200) — 잘린 줄 모르고 '이력이 없다'고 판단하지 않게 알린다 -->
        <b-alert v-if="!loading && !loadError && logs.length >= limit" show variant="info"
                 class="py-2 d-flex align-items-center justify-content-between"
        >
            <span v-if="limit < maxLimit">최근 {{ limit }}건만 표시합니다. 업체 ID로 좁히거나 더 불러오세요.</span>
            <span v-else>최근 {{ limit }}건까지만 볼 수 있습니다. 더 오래된 이력은 업체 ID로 좁혀 찾으세요.</span>
            <b-button v-if="limit < maxLimit" size="sm" variant="outline-primary" @click="loadMore">
                더 보기 (최근 {{ maxLimit }}건)
            </b-button>
        </b-alert>

        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄. 폭은 머리가 정한다.
             이력 ID 가 실행 결과 · URL(log_id)의 번호와 같고 최대 200건 한 화면이라 No. 열은 따로 두지 않는다 -->
        <b-table :items="logs" :fields="fields" :busy="loading" responsive show-empty
                 table-class="ds-table ds-table-fixed remap-history-table"
        >
            <template #empty>
                <PageEmptyComponent compact text="실행 이력이 없습니다." :error="loadError" @retry="load"/>
            </template>
            <template #table-busy>
                <div class="text-center py-3">
                    <b-spinner small class="mr-1"/>불러오는 중...
                </div>
            </template>
            <!-- 메인 칸 = 백업 상세 창 -->
            <template #cell(id)="row">
                <button type="button" class="ds-detail-link" @click="showDetail(row.item.id)">#{{ row.value }}</button>
            </template>
            <template #cell(status)="row">
                <span class="ds-status" :class="`ds-status-${row.value === 'EXECUTED' ? 'success' : 'danger'}`"
                      :title="row.value"
                >{{ statusLabel(row.value) }}</span>
            </template>
            <template #cell(campaign)="row">
                <span class="text-monospace">{{ row.item.from_campaign_id }} &rarr; {{ row.item.to_campaign_id }}</span>
            </template>
            <template #cell(created_by)="row">
                <span v-if="row.value !== ''">{{ row.value }}</span>
                <span v-else class="ds-sub">-</span>
            </template>
            <template #cell(created_at)="row">
                {{ formatDate(row.value) }}
            </template>
            <template #head(actions)="data">
                <span class="sr-only">{{ data.label }}</span>
            </template>
            <template #cell(actions)="row">
                <button type="button" class="ds-row-icon-btn" :aria-label="`이력 #${row.item.id} 백업 상세`"
                        title="백업 상세" @click="showDetail(row.item.id)"
                >
                    <AppIcon name="chevron-right"/>
                </button>
            </template>
        </b-table>

        <!-- ── 백업 상세 ── -->
        <b-modal v-model="detailOpen" size="xl" :title="detailTitle"
                 ok-only ok-title="닫기" scrollable @hide="onDetailHide"
        >
            <div v-if="detailLoading" class="text-center py-4">
                <b-spinner small class="mr-1"/>불러오는 중...
            </div>
            <div v-else-if="detailError">
                <b-alert variant="danger" show>{{ detailError }}</b-alert>
            </div>
            <div v-else-if="detail">
                <b-alert variant="info" show class="py-2">
                    <i class="bi bi-info-circle mr-1"></i>
                    복구는 자동으로 되지 않습니다. 아래 내용을 근거로 담당자가 직접 복원해야 합니다.
                </b-alert>

                <b-row class="mb-3 small">
                    <b-col md="3">업체: <strong>{{ detail.company_id }}</strong></b-col>
                    <b-col md="3">
                        캠페인: <strong>{{ detail.from_campaign_id }} &rarr; {{ detail.to_campaign_id }}</strong>
                    </b-col>
                    <b-col md="3">실행자: <strong>{{ detail.created_by }}</strong></b-col>
                    <b-col md="3">{{ formatDate(detail.created_at) }}</b-col>
                </b-row>

                <b-row class="text-center mb-3">
                    <b-col v-for="(v, k) in detail.backup_counts" :key="k">
                        <div class="count-box">
                            <div class="count-label">{{ countLabel(k) }}</div>
                            <div class="count-value">{{ v }}</div>
                        </div>
                    </b-col>
                </b-row>

                <b-tabs content-class="mt-3" small>
                    <b-tab :title="`삭제된 답변 (${backupSubs.length})`">
                        <b-table v-if="backupSubs.length" :items="backupSubs" :fields="subFields"
                                 small striped responsive sticky-header="420px"
                        >
                            <template #cell(sub_data_contents)="row">
                                <code class="small">{{ truncate(row.value) }}</code>
                            </template>
                        </b-table>
                        <p v-else class="text-muted mb-0">삭제된 답변이 없습니다.</p>
                    </b-tab>
                    <b-tab :title="`삭제된 감사자 평가 (${backupEvals.length})`">
                        <b-table v-if="backupEvals.length" :items="backupEvals" :fields="evalFields"
                                 small striped responsive sticky-header="420px"
                        />
                        <p v-else class="text-muted mb-0">삭제된 평가가 없습니다.</p>
                    </b-tab>
                    <b-tab :title="`삭제된 첨부 (${backupAttachments.length})`">
                        <b-table v-if="backupAttachments.length" :items="backupAttachments"
                                 :fields="attachmentFields" small striped responsive sticky-header="420px"
                        />
                        <p v-else class="text-muted mb-0">삭제된 첨부가 없습니다.</p>
                    </b-tab>
                    <b-tab title="실행 요약">
                        <pre class="summary-json">{{ prettySummary }}</pre>
                    </b-tab>
                </b-tabs>
            </div>
        </b-modal>
    </div>
</template>

<script>
/**
 * 답변 보존 캠페인 재배정 — 실행 이력 / 백업 회수 탭.
 *
 * 복구 기능은 없다(수동 SQL). 이 화면이 삭제된 원본을 꺼내는 유일한 경로이며,
 * 여기 내용을 근거로 담당자가 복원하거나 "무엇이 지워졌는지"를 확인한다.
 *
 * 목록은 요약만 받고(backups=false 상당), 백업 전문은 상세를 열 때만 받는다 —
 * 삭제 답변이 많으면 응답이 수백 KB 라 목록에 싣기엔 무겁다.
 *
 * URL: ?tab=history&company_id=(업체 필터)&log_id=(열린 상세). 상세는 목록과 상관없이 id 로 연다
 * (최근 50건 밖의 이력도 링크로 열린다). lazy 탭이라 탭을 오갈 때마다 다시 마운트되어 새로 읽는다.
 */
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {
    fetchRemapLogDetail,
    fetchRemapLogs,
    remapErrorMessage,
} from '@shared_utils/api/campaignRemap';

const DEFAULT_LIMIT = 50;
// 백엔드 get_logs 상한. offset 이 없어 그보다 오래된 이력은 업체 필터로만 찾는다
const MAX_LIMIT = 200;
// 1 이상 정수 문자열만 (배열·소수·음수는 무시)
const queryId = v => {
    const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
};

export default {
    name: 'RemapHistoryTab',
    components: {PageEmptyComponent},
    data() {
        return {
            logs: [],
            loading: false,
            loaded: false,
            loadError: '',
            listSeq: 0,
            limit: DEFAULT_LIMIT,
            maxLimit: MAX_LIMIT,
            companyFilter: '',
            appliedCompanyId: null,
            detailLogId: null,
            detailSeq: 0,
            detailOpen: false,
            detailLoading: false,
            detailError: '',
            detail: null,
            // DS 표 규칙: 폭을 안 적은 캠페인 변경이 남는 폭을 갖는다. 실행자는 users.id(숫자)
            fields: [
                {key: 'id', label: '이력 ID', thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: {width: '88px'}},
                {key: 'company_id', label: '업체 ID', thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: {width: '100px'}},
                {key: 'campaign', label: '캠페인 변경', tdClass: 'ds-cell-nowrap'},
                {key: 'status', label: '상태', thStyle: {width: '100px'}},
                {key: 'created_by', label: '실행자', thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: {width: '100px'}},
                {key: 'created_at', label: '실행 시각', tdClass: 'ds-cell-nowrap', thStyle: {width: '168px'}},
                // 아이콘 28 + 좌우 16 = 60
                {key: 'actions', label: '상세', tdClass: 'text-right', thStyle: {width: '60px'}},
            ],
            subFields: [
                {key: 'id', label: 'ID'},
                {key: 'sub_code', label: '문항 코드'},
                {key: 'question_code', label: '질문 코드'},
                {key: 'sub_data_contents', label: '답변 내용'},
            ],
            evalFields: [
                {key: 'id', label: 'ID'},
                {key: 'ev_id', label: '평가요소'},
                {key: 'satisfied', label: '충족'},
                {key: 'author_id', label: '평가자'},
                {key: 'comment', label: '코멘트'},
            ],
            attachmentFields: [
                {key: 'id', label: 'ID'},
                {key: 'filename', label: '파일명'},
                {key: 's3_key', label: 'S3 키'},
            ],
        };
    },
    computed: {
        backupSubs() {
            return (this.detail && this.detail.backups && this.detail.backups.deleted_subs) || [];
        },
        backupEvals() {
            return (this.detail && this.detail.backups
                && this.detail.backups.deleted_evaluations) || [];
        },
        backupAttachments() {
            return (this.detail && this.detail.backups
                && this.detail.backups.deleted_attachment_storage) || [];
        },
        prettySummary() {
            if (!this.detail) return '';
            return JSON.stringify(this.detail.result_summary, null, 2);
        },
        detailTitle() {
            return this.detailLogId ? `삭제된 원본 (before-image) · 이력 #${this.detailLogId}` : '삭제된 원본 (before-image)';
        },
    },
    watch: {
        // 같은 화면 안의 query 변화(뒤로가기·링크). 실행 탭으로 떠나는 중이면 무시한다
        '$route.query'() {
            if (this.isActive()) this.applyQuery();
        },
    },
    mounted() {
        // 탭 버튼을 누르면 탭이 먼저 그려지고 URL 은 뒤에 바뀔 수 있다 — 그때는 실행 탭의 query 를 읽지 않고 watcher 를 기다린다
        if (this.isActive()) this.applyQuery();
    },
    beforeDestroy() {
        // 떠난 뒤 늦게 끝난 요청이 다른 탭·화면의 URL 을 고치지 않게 한다
        this.left = true;
        this.listSeq++;
        this.detailSeq++;
    },
    methods: {
        isActive() {
            return !this.left && this.$route.query.tab === 'history';
        },
        /** URL → 화면. 업체 필터가 바뀌면(또는 처음이면) 목록을, log_id 가 있으면 그 상세를 연다. */
        applyQuery() {
            const q = this.$route.query;
            const companyId = queryId(q.company_id);
            if (!this.loaded || companyId !== this.appliedCompanyId) {
                this.loaded = true;
                this.companyFilter = companyId ? String(companyId) : '';
                this.appliedCompanyId = companyId;
                this.limit = DEFAULT_LIMIT;
                this.load();
            }
            const logId = queryId(q.log_id);
            if (logId && (logId !== this.detailLogId || !this.detailOpen)) this.openDetail(logId);
            else if (!logId && this.detailOpen) this.detailOpen = false;
            // 빈 값·잘못된 값(?company_id=&log_id=abc)은 URL 에서 지운다 (replace, 실제로 다를 때만)
            const fixed = {...q};
            if ('company_id' in fixed && !companyId) delete fixed.company_id;
            if ('log_id' in fixed && !logId) delete fixed.log_id;
            if (Object.keys(fixed).length !== Object.keys(q).length) this.$router.replace({query: fixed}).catch(() => {});
        },
        // 조회·Enter·필터 해제 (push). 조건이 같으면 다시 읽기만 한다
        submitFilter() {
            const raw = String(this.companyFilter || '').trim();
            const companyId = queryId(raw);
            if (raw && !companyId) {
                this.$EventBus.$emit('toast-warning', '업체 ID 확인', '업체 ID는 1 이상의 정수로 입력하세요.');
                return;
            }
            if (companyId === this.appliedCompanyId) {
                this.load();
                return;
            }
            const query = {tab: 'history'};
            if (companyId) query.company_id = String(companyId);
            this.$router.push({query}).catch(() => {});
        },
        clearFilter() {
            this.companyFilter = '';
            this.submitFilter();
        },
        loadMore() {
            this.limit = MAX_LIMIT;
            this.load();
        },
        async load() {
            const seq = ++this.listSeq;
            this.loading = true;
            this.loadError = '';
            try {
                const logs = await fetchRemapLogs({companyId: this.appliedCompanyId, limit: this.limit});
                if (seq === this.listSeq) this.logs = logs;
            } catch (e) {
                if (seq !== this.listSeq || e?.response?.status === 401) return;
                // 토스트를 놓치면 '이력이 없다'로 오해하므로 목록 자리에 남긴다
                this.logs = [];
                this.loadError = remapErrorMessage(e, '실행 이력을 불러오지 못했습니다.');
            } finally {
                if (seq === this.listSeq) this.loading = false;
            }
        },
        // 행의 '백업 보기' (push)
        showDetail(logId) {
            this.openDetail(logId);
            if (queryId(this.$route.query.log_id) !== logId) {
                this.$router.push({query: {...this.$route.query, log_id: String(logId)}}).catch(() => {});
            }
        },
        async openDetail(logId) {
            const seq = ++this.detailSeq;
            this.detailLogId = logId;
            this.detailOpen = true;
            this.detailLoading = true;
            this.detailError = '';
            this.detail = null;
            try {
                const detail = await fetchRemapLogDetail(logId);
                if (seq === this.detailSeq) this.detail = detail;
            } catch (e) {
                if (seq !== this.detailSeq || e?.response?.status === 401) return;
                this.detailError = remapErrorMessage(e, '백업을 불러오지 못했습니다.');
            } finally {
                if (seq === this.detailSeq) this.detailLoading = false;
            }
        },
        // 닫으면 URL 에서 log_id 를 뺀다 (replace). 닫힌 것이 URL 의 상세일 때만 — 그새 다른 상세로 바뀌었으면 두지 않는다
        onDetailHide() {
            const closed = this.detailLogId;
            this.detailSeq++;
            if (!this.isActive() || !closed || queryId(this.$route.query.log_id) !== closed) return;
            const query = {...this.$route.query};
            delete query.log_id;
            this.$router.replace({query}).catch(() => {});
        },
        // 상태 원문(EXECUTED|FAILED)은 title 로 남긴다. 모르는 값은 원문 그대로
        statusLabel(status) {
            return {EXECUTED: '실행됨', FAILED: '실패'}[status] || status;
        },
        countLabel(key) {
            return {
                deleted_subs: '답변',
                deleted_reviews: '검토의견',
                deleted_evaluations: '감사자 평가',
                deleted_attachment_storage: '첨부',
                deleted_sub_attachments: '첨부 링크',
            }[key] || key;
        },
        truncate(value, len = 120) {
            if (value === null || value === undefined) return '';
            const s = typeof value === 'string' ? value : JSON.stringify(value);
            return s.length > len ? `${s.slice(0, len)}...` : s;
        },
        formatDate(iso) {
            if (!iso) return '';
            return String(iso).replace('T', ' ').slice(0, 19);
        },
    },
};
</script>

<style scoped>
.count-box {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 8px;
}

.count-label {
    font-size: 0.75rem;
    color: #6c757d;
}

.count-value {
    font-size: 1.25rem;
    font-weight: 600;
}

.summary-json {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px;
    max-height: 420px;
    overflow: auto;
    font-size: 0.8rem;
}
</style>

<style>
/* 고정 열 합 616(이력 ID 88 · 업체 ID 100 · 상태 100 · 실행자 100 · 실행 시각 168 · 상세 60) + 캠페인 변경 ~164.
   본문 폭 약 1026(1366 메뉴 펼침 1074 - 화면 p-4 좌우 48) 안 — 그보다 좁으면 가로 스크롤 */
.remap-history-table {
    min-width: 780px;
}
</style>
